import { getLaunchDarklyKey } from '@utils/environment';
import { initialize, LDContext } from 'launchdarkly-js-client-sdk';
import { useEffect, useMemo, useState } from 'react';

import { FeatureFlags } from '.';
import { emptyClient } from './empty-client';
import { FlagContext } from './flag-context';

type FlagUpdate = Record<string, { current: unknown; previous: unknown }>;

export const FlagProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [client, setClient] = useState(emptyClient);
  const [flags, setFlags] = useState<FeatureFlags>({} as FeatureFlags);
  window._ldFlags = flags;

  const [flagChangeCount, setFlagChangeCount] = useState(0);
  const [areFlagsLoading, setFlagsLoading] = useState(
    Boolean(getLaunchDarklyKey()),
  );

  useEffect(() => {
    const ldKey = getLaunchDarklyKey();
    if (!ldKey) {
      return;
    }

    // Allow DataDog test bots to proceed without a user,
    // but require a valid userId for all other scenarios.
    const ldContext: LDContext = window._DATADOG_SYNTHETICS_BROWSER
      ? { kind: 'user', key: 'synthetic-user' }
      : { kind: 'user', key: 'real-user' };

    const ldClient = initialize(ldKey, ldContext, {
      bootstrap: 'localStorage',
      streaming: true,
    });

    setClient(ldClient);

    ldClient.on('ready', () => {
      setFlags((prevFlags) => ({
        ...prevFlags,
        ...ldClient.allFlags(),
      }));
      setFlagsLoading(false);
    });

    ldClient.on('change', (newFlags: FlagUpdate) => {
      setFlags((prevFlags) => ({
        ...prevFlags,
        ...Object.fromEntries(
          Object.entries(newFlags).map(([key, { current }]) => [key, current]),
        ),
      }));
      setFlagChangeCount((count) => count + 1);
    });
  }, []);

  // Determine if the flags are loaded
  // This is more an art than a science because a user can get between
  // one and two change events initially from LaunchDarkly. The first
  // change event has the default values whereas the second has user's values.
  useEffect(() => {
    if (flagChangeCount === 1) {
      // When the second change never happens
      setTimeout(() => setFlagsLoading(false), 1000);
    } else if (flagChangeCount >= 2) {
      setFlagsLoading(false);
    }
  }, [flagChangeCount]);

  const value = useMemo(
    () => ({ client, flags, areFlagsLoading }),
    [client, flags, areFlagsLoading],
  );

  return <FlagContext.Provider value={value}>{children}</FlagContext.Provider>;
};
